/* Timber and colour — interactive species cards (2026-08-05)

   Rearranges each species card into: the species name, its note, one large
   viewer, a row of three colour chips, and the sample link for whichever
   colour is showing. Clicking a chip swaps that card's viewer. Each species
   holds its own selection — changing Tōtara leaves Rimu and Beech alone.

   Notes on what this deliberately preserves:

   - The lightbox. Its script binds a click listener to each element carrying
     [data-timber-preview] at page load. Listeners travel with the node, so
     moving those buttons into the viewer keeps the lightbox working, and
     clicking the large image still opens it.
   - The sample links. Each colour label links to a real product variant on
     /products/product-sample. All nine stay in the DOM; the one matching the
     current selection is the one shown, so every variant is two clicks away.
   - The photography. Nothing here touches colour — no filters or opacity on
     the timber in any state.

   The .is-timber-switcher class is added only after a card has been rebuilt
   successfully, and every style depends on it, so a failure here leaves the
   section exactly as the CSS alone renders it. */

(function () {
  'use strict';

  var GRID = '.innate-dining-timber-grid';
  var CARD = '.innate-dining-timber-grid__species-card';
  var PREVIEW = '.innate-dining-timber-grid__preview';
  var LINK = '.innate-dining-timber-grid__variant-link';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function textOf(node) {
    return node ? (node.textContent || '').trim() : '';
  }

  /* A chip needs to look like the colour it selects. Rather than rebuild that
     from settings, clone whatever the preview already renders — an <img> for
     Clear, a sprite-backed span for the other two — so the chip and the
     viewer can never disagree. */
  function chipVisual(preview) {
    var source = preview.querySelector('img, .innate-dining-timber-grid__swatch-image');
    var wrap = document.createElement('span');
    wrap.className = 'innate-timber-chip__swatch';
    if (source) {
      var clone = source.cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      if (clone.tagName === 'IMG') clone.setAttribute('alt', '');
      wrap.appendChild(clone);
    }
    return wrap;
  }

  /* Cursor-follow tilt on the large image, after the TiltedCard component from
     reactbits. Written by hand rather than pulled in: this page has no React,
     and the effect is a transform driven by pointer position, so a dependency
     would cost far more than it saves.

     The reference uses framer-motion springs; here the weight comes from a
     long CSS transition on transform, which the mousemove handler keeps
     retargeting. The result trails the cursor the same way a heavy spring
     does, at no runtime cost.

     Skipped entirely on touch and under reduced motion — a tilt that keys off
     a hovering pointer has nothing to respond to on a phone. */
  var AMPLITUDE = 12;
  var HOVER_SCALE = 1.045;

  function addTilt(viewer) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none), (pointer: coarse)').matches) return;

    var frame = 0;

    viewer.addEventListener('mousemove', function (event) {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = 0;
        var rect = viewer.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        var offsetX = event.clientX - rect.left - rect.width / 2;
        var offsetY = event.clientY - rect.top - rect.height / 2;
        var rotateX = (offsetY / (rect.height / 2)) * -AMPLITUDE;
        var rotateY = (offsetX / (rect.width / 2)) * AMPLITUDE;
        viewer.style.transform =
          'rotateX(' + rotateX.toFixed(2) + 'deg) rotateY(' + rotateY.toFixed(2) + 'deg) scale(' + HOVER_SCALE + ')';
      });
    });

    viewer.addEventListener('mouseenter', function () {
      viewer.classList.add('is-tilting');
    });

    viewer.addEventListener('mouseleave', function () {
      if (frame) { window.cancelAnimationFrame(frame); frame = 0; }
      viewer.classList.remove('is-tilting');
      viewer.style.transform = '';
    });
  }

  function buildCard(card, index) {
    var previews = [].slice.call(card.querySelectorAll(PREVIEW));
    var links = [].slice.call(card.querySelectorAll(LINK));
    if (previews.length < 2) return false;

    var name = card.querySelector('.innate-dining-timber-grid__species-name');
    var note = card.querySelector('.innate-dining-timber-grid__species-note');

    var viewer = document.createElement('div');
    viewer.className = 'innate-timber-viewer';

    var chips = document.createElement('div');
    chips.className = 'innate-timber-chips';
    chips.setAttribute('role', 'radiogroup');
    chips.setAttribute('aria-label', textOf(name) + ' colour');

    var sample = document.createElement('div');
    sample.className = 'innate-timber-sample';

    var buttons = [];

    previews.forEach(function (preview, i) {
      viewer.appendChild(preview);

      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'innate-timber-chip';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', 'false');
      chip.id = 'innate-timber-chip-' + index + '-' + i;

      var label = document.createElement('span');
      label.className = 'innate-timber-chip__label';
      label.textContent = textOf(links[i]) || (preview.getAttribute('aria-label') || 'Colour');

      chip.appendChild(chipVisual(preview));
      chip.appendChild(label);
      chip.addEventListener('click', function () { select(i); });
      chips.appendChild(chip);
      buttons.push(chip);

      if (links[i]) sample.appendChild(links[i]);
    });

    function select(active) {
      previews.forEach(function (p, i) {
        p.classList.toggle('is-active', i === active);
        // Only the visible preview should be reachable by keyboard, or tab
        // order would run through three invisible buttons per card.
        p.tabIndex = i === active ? 0 : -1;
        p.setAttribute('aria-hidden', i === active ? 'false' : 'true');
      });
      buttons.forEach(function (b, i) {
        b.setAttribute('aria-checked', i === active ? 'true' : 'false');
        b.tabIndex = i === active ? 0 : -1;
      });
      links.forEach(function (l, i) {
        if (l) l.classList.toggle('is-active', i === active);
      });
    }

    chips.addEventListener('keydown', function (event) {
      var dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
              : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
      if (!dir) return;
      event.preventDefault();
      var current = buttons.findIndex(function (b) { return b.getAttribute('aria-checked') === 'true'; });
      if (current < 0) current = 0;
      var next = (current + dir + buttons.length) % buttons.length;
      select(next);
      buttons[next].focus();
    });

    // Clear leads, so the three species can be compared on the same finish.
    select(0);

    var visuals = card.querySelector('.innate-dining-timber-grid__visuals');
    if (visuals && visuals.parentNode) visuals.parentNode.removeChild(visuals);

    if (note && note.parentNode === card) {
      card.insertBefore(viewer, note.nextSibling);
    } else {
      card.appendChild(viewer);
    }
    viewer.parentNode.insertBefore(chips, viewer.nextSibling);
    chips.parentNode.insertBefore(sample, chips.nextSibling);

    addTilt(viewer);

    return true;
  }

  ready(function () {
    var grids = document.querySelectorAll(GRID);
    if (!grids.length) return;

    grids.forEach(function (grid) {
      if (grid.dataset.timberSwitcher === 'true') return;

      var cards = [].slice.call(grid.querySelectorAll(CARD));
      if (!cards.length) return;

      var built = 0;
      cards.forEach(function (card, i) {
        try { if (buildCard(card, i)) built++; }
        catch (e) { /* leave this card as it was */ }
      });

      // Only claim the layout if every card converted. A half-converted grid
      // would look worse than the one we started with.
      if (built === cards.length) {
        grid.dataset.timberSwitcher = 'true';
        grid.classList.add('is-timber-switcher');
      }
    });
  });
})();
